import type { ComponentType } from 'react'
import { getEntityTerminologyStatic } from '@/lib/terminology/static-terminology'
import { getModuleNavigationPath } from '@/lib/utils/field-navigation'
import {
  BookOpenIcon,
  BuildingsIcon,
  CalendarIcon,
  CheckSquareIcon,
  FilesIcon,
  FileTextIcon,
  FolderOpenIcon,
  HandshakeIcon,
  ListBulletsIcon,
  PackageIcon,
  ReceiptIcon,
  SquaresFourIcon,
  TruckIcon,
  UserIcon,
} from '@phosphor-icons/react'

import type { ModuleLink } from '@scrambl/shared'
import { isValidModuleType, ModuleTypeEnum } from '@scrambl/shared'

type ModuleLinkMeta = {
  icon: ComponentType<{ className?: string }>
  bgClass: string
  textClass: string
  label: string
}

const MODULE_LINK_VISUALS: Record<string, Omit<ModuleLinkMeta, 'label'>> = {
  ENTITY: {
    icon: BuildingsIcon,
    bgClass: 'bg-amber-500/10',
    textClass: 'text-amber-600 dark:text-amber-500',
  },
  PROJECT: {
    icon: FolderOpenIcon,
    bgClass: 'bg-blue-500/10',
    textClass: 'text-blue-600 dark:text-blue-400',
  },
  TASK: {
    icon: CheckSquareIcon,
    bgClass: 'bg-emerald-500/10',
    textClass: 'text-emerald-600 dark:text-emerald-400',
  },
  TASK_GROUP: {
    icon: SquaresFourIcon,
    bgClass: 'bg-cyan-500/10',
    textClass: 'text-cyan-600 dark:text-cyan-400',
  },
  TICKET: {
    icon: FileTextIcon,
    bgClass: 'bg-red-500/10',
    textClass: 'text-red-600 dark:text-red-400',
  },
  LEAD: {
    icon: UserIcon,
    bgClass: 'bg-purple-500/10',
    textClass: 'text-purple-600 dark:text-purple-400',
  },
  DEAL: {
    icon: HandshakeIcon,
    bgClass: 'bg-green-500/10',
    textClass: 'text-green-600 dark:text-green-400',
  },
  CONTACT: {
    icon: UserIcon,
    bgClass: 'bg-cyan-500/10',
    textClass: 'text-cyan-600 dark:text-cyan-400',
  },
  EVENT: {
    icon: CalendarIcon,
    bgClass: 'bg-orange-500/10',
    textClass: 'text-orange-600 dark:text-orange-400',
  },
  CONTRACT: {
    icon: FileTextIcon,
    bgClass: 'bg-indigo-500/10',
    textClass: 'text-indigo-600 dark:text-indigo-400',
  },
  INVOICE: {
    icon: ReceiptIcon,
    bgClass: 'bg-rose-500/10',
    textClass: 'text-rose-600 dark:text-rose-400',
  },
  INVENTORY: {
    icon: PackageIcon,
    bgClass: 'bg-lime-500/10',
    textClass: 'text-lime-700 dark:text-lime-400',
  },
  SUPPLIER: {
    icon: TruckIcon,
    bgClass: 'bg-teal-500/10',
    textClass: 'text-teal-600 dark:text-teal-400',
  },
  NOTE: {
    icon: FileTextIcon,
    bgClass: 'bg-violet-500/10',
    textClass: 'text-violet-600 dark:text-violet-400',
  },
  DOCUMENT: {
    icon: FilesIcon,
    bgClass: 'bg-sky-500/10',
    textClass: 'text-sky-600 dark:text-sky-400',
  },
  CUSTOM_COLLECTION: {
    icon: ListBulletsIcon,
    bgClass: 'bg-slate-500/10',
    textClass: 'text-slate-600 dark:text-slate-400',
  },
  RESOURCE: {
    icon: PackageIcon,
    bgClass: 'bg-purple-500/10',
    textClass: 'text-purple-600 dark:text-purple-400',
  },
  ORGANIZATION: {
    icon: SquaresFourIcon,
    bgClass: 'bg-indigo-500/10',
    textClass: 'text-indigo-600 dark:text-indigo-400',
  },
  MEMBER: {
    icon: UserIcon,
    bgClass: 'bg-teal-500/10',
    textClass: 'text-teal-600 dark:text-teal-400',
  },
  KNOWLEDGE_BASE: {
    icon: BookOpenIcon,
    bgClass: 'bg-fuchsia-500/10',
    textClass: 'text-fuchsia-600 dark:text-fuchsia-400',
  },
}

const MODULE_LINK_LABELS: Record<string, string> = {
  PROJECT: 'Project',
  TASK: 'Task',
  TASK_GROUP: 'Task Group',
  TICKET: 'Ticket',
  LEAD: 'Lead',
  DEAL: 'Deal',
  CONTACT: 'Contact',
  EVENT: 'Event',
  CONTRACT: 'Contract',
  INVOICE: 'Invoice',
  INVENTORY: 'Inventory',
  SUPPLIER: 'Supplier',
  NOTE: 'Note',
  DOCUMENT: 'Document',
  CUSTOM_COLLECTION: 'Collection',
  RESOURCE: 'Resource',
  ORGANIZATION: 'Organization',
  MEMBER: 'Person',
  KNOWLEDGE_BASE: 'Knowledge article',
  TIME_ENTRY: 'Time entry',
  TIMESHEET_APPROVAL: 'Timesheet approval',
  MY_TIME: 'My Time',
}

const DEFAULT_META: ModuleLinkMeta = {
  icon: FolderOpenIcon,
  bgClass: 'bg-muted',
  textClass: 'text-muted-foreground',
  label: 'Record',
}

export function getModuleLinkMeta(
  moduleType: ModuleTypeEnum | string | null | undefined,
  options?: { entityLabel?: string },
): ModuleLinkMeta {
  if (!moduleType) {
    return DEFAULT_META
  }

  const normalizedType = String(moduleType).toUpperCase()
  const visuals = MODULE_LINK_VISUALS[normalizedType] ?? DEFAULT_META
  const label =
    normalizedType === 'ENTITY'
      ? options?.entityLabel || getEntityTerminologyStatic().Singular
      : MODULE_LINK_LABELS[normalizedType] || normalizedType

  return {
    icon: visuals.icon,
    bgClass: visuals.bgClass,
    textClass: visuals.textClass,
    label,
  }
}

export function resolveModuleLinkPath(params: {
  moduleLink?: ModuleLink | null
  moduleType?: ModuleTypeEnum | string | null
  moduleId?: string | null
  modulePath?: string | null
  orgSlug?: string | null
}): string | null {
  const modulePath = params.moduleLink?.modulePath ?? params.modulePath

  // If we have a stored modulePath, ensure it has the org slug prefix
  if (modulePath && params.orgSlug) {
    // Check if the path already starts with the org slug
    const orgSlugPrefix = `/${params.orgSlug}/`
    if (modulePath.startsWith(orgSlugPrefix)) {
      return modulePath
    }
    // If path starts with /, prepend org slug
    if (modulePath.startsWith('/')) {
      return `/${params.orgSlug}${modulePath}`
    }
    // Otherwise prepend full prefix
    return `/${params.orgSlug}/${modulePath}`
  }

  // If we have modulePath but no orgSlug, we can't build a proper path
  // Fall through to try building from moduleType/moduleId

  const moduleType = params.moduleLink?.moduleType ?? params.moduleType
  const moduleId = params.moduleLink?.moduleId ?? params.moduleId
  if (!moduleType || !moduleId || !params.orgSlug) {
    return null
  }

  if (!isValidModuleType(moduleType)) {
    return null
  }

  return getModuleNavigationPath(
    moduleType as ModuleTypeEnum,
    moduleId,
    params.orgSlug,
  )
}

export function toModuleLink(value: {
  type: string
  id: string
  name?: string | null
  path?: string | null
}): ModuleLink {
  return {
    moduleType: value.type as ModuleTypeEnum,
    moduleId: value.id,
    moduleName: value.name ?? null,
    modulePath: value.path ?? null,
  }
}
